import React from 'react'
import KatexRenderer from './KatexRenderer'

// Type for variables from quiz data
export interface VarValue {
  type?: string
  result?: number | string
  array?: (number | string)[]
  min?: string | number
  max?: string | number
}

// Allow any Record<string, unknown> to be cast to VarRecord
export type VarRecord = Record<string, VarValue | number | string | unknown[]>

// Basic formatter to handle newlines and verify visibility
// Updated to support Katex rendering across all text outputs.
// Now also supports variable substitution for quiz data.
export const FormattedText: React.FC<{
  text: string | null | undefined
  className?: string
  vars?: Record<string, unknown> | null
}> = ({ text, className = '', vars }) => {
  if (!text) return null

  // First normalize text
  let normalizedText = text.normalize ? text.normalize('NFC') : text

  // Substitute variables if provided (this also calls preprocessLegacyMarkup internally)
  if (vars && Object.keys(vars).length > 0) {
    normalizedText = substituteVariables(normalizedText, vars as VarRecord)
  } else {
    // Always apply preprocessLegacyMarkup even without vars
    normalizedText = preprocessLegacyMarkup(normalizedText)
  }

  return (
    <div className={className}>
      <KatexRenderer text={normalizedText} />
    </div>
  )
}

// Helper function to get variable value
const getVarValue = (
  vars: Record<string, VarValue | number | string | unknown[]>,
  key: string,
  index?: number | string
): number | string | undefined => {
  const variable = vars[key]
  if (variable === undefined || variable === null) return undefined

  if (index !== undefined) {
    const resolvedIndex = typeof index === 'number' ? index : resolveArrayIndex(vars, index)
    if (resolvedIndex === undefined) return undefined

    // Array access
    if (typeof variable === 'object' && 'array' in variable && Array.isArray(variable.array)) {
      return variable.array[resolvedIndex]
    }
    if (Array.isArray(variable)) {
      return variable[resolvedIndex] as number | string
    }
    if (typeof variable === 'object' && 'result' in variable && Array.isArray(variable.result)) {
      return variable.result[resolvedIndex]
    }
  } else {
    // Simple variable
    if (typeof variable === 'number' || typeof variable === 'string') {
      return variable
    }
    if (typeof variable === 'object' && 'result' in variable && variable.result !== undefined) {
      const result = variable.result
      if (typeof result === 'number') {
        return Number.isInteger(result) ? Math.floor(result) : result
      }
      return result as string
    }
  }
  return undefined
}

const resolveArrayIndex = (
  vars: Record<string, VarValue | number | string | unknown[]>,
  index: string
): number | undefined => {
  const trimmed = index.trim()
  if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10)

  const nestedArrayMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\[([^\]]+)\]$/)
  const value = nestedArrayMatch
    ? getVarValue(vars, nestedArrayMatch[1], nestedArrayMatch[2])
    : getVarValue(vars, trimmed)

  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10)
    return Number.isNaN(parsed) ? undefined : parsed
  }
  return undefined
}

// Helper to safely evaluate simple math expressions with variable substitution
const evaluateExpression = (
  expr: string,
  vars: Record<string, VarValue | number | string | unknown[]>
): string | null => {
  try {
    // First, substitute all variable references in the expression
    // Replace array access: a[0] -> actual value
    let substituted = expr.replace(
      /([a-zA-Z_][a-zA-Z0-9_]*)\[([^\]]+)\]/g,
      (match, key, indexStr) => {
        const value = getVarValue(vars, key, indexStr)
        return value !== undefined ? String(value) : match
      }
    )

    // Replace simple variables: a, a1 -> actual value
    substituted = substituted.replace(/([a-zA-Z_][a-zA-Z0-9_]*)/g, (match, key) => {
      // Skip if it looks like a function or already a number
      if (/^\d/.test(match)) return match
      const value = getVarValue(vars, key)
      return value !== undefined ? String(value) : match
    })

    // Check if all variables were substituted (no remaining letters except in numbers)
    if (/[a-zA-Z_]/.test(substituted.replace(/\d+\.?\d*/g, ''))) {
      return null // Still has unsubstituted variables
    }

    // Safely evaluate the expression (only allows numbers and basic operators)

    const result = new Function(`return (${substituted})`)()

    if (typeof result === 'number') {
      // Format result: if integer, no decimals; otherwise round to reasonable precision
      if (Number.isInteger(result)) {
        return String(result)
      }
      // Round to avoid floating point issues
      return String(Math.round(result * 1000000) / 1000000)
    }
    return String(result)
  } catch {
    return null
  }
}

// Replace []{{...}} -> {{...}} pattern (from legacy quiz-parser)
export const replaceSquareBracketPattern = (str: string): string => {
  const regex = /\[\](\{\{.*?\}\})/g
  return str.replace(regex, '$1')
}

// Preprocess legacy markup tags to LaTeX format
// This should be called before any text splitting or KaTeX rendering
export const preprocessLegacyMarkup = (text: string): string => {
  if (!text) return text || ''

  let processed = text

  // Replace []{{...}} -> {{...}} for blank compatibility
  processed = replaceSquareBracketPattern(processed)

  // Convert fullwidth Unicode characters to ASCII equivalents
  // Note: ×/÷ are converted to \times/\div ONLY inside $$...$$ blocks (see below)
  processed = processed
    .replace(/＋/g, '+') // U+FF0B fullwidth plus
    .replace(/－/g, '-') // U+FF0D fullwidth minus
    .replace(/＝/g, '=') // U+FF1D fullwidth equals
    .replace(/＜/g, '<') // U+FF1C fullwidth less-than
    .replace(/＞/g, '>') // U+FF1E fullwidth greater-than
    .replace(/（/g, '(') // U+FF08 fullwidth left paren
    .replace(/）/g, ')') // U+FF09 fullwidth right paren
    .replace(/［/g, '[') // U+FF3B fullwidth left bracket
    .replace(/］/g, ']') // U+FF3D fullwidth right bracket
    .replace(/｛/g, '{') // U+FF5B fullwidth left brace
    .replace(/｝/g, '}') // U+FF5D fullwidth right brace
    // ×/÷ are NOT converted globally - they render fine as Unicode in plain text
    // Only convert inside $$...$$ blocks (LaTeX math mode) below
    .replace(/−/g, '-') // U+2212 minus sign
    .replace(/–/g, '-') // U+2013 en-dash
    .replace(/—/g, '-') // U+2014 em-dash
    .replace(/′/g, "'") // prime
    .replace(/″/g, "''") // double prime
    .replace(/'/g, "'") // right single quote
    .replace(/'/g, "'") // left single quote
    .replace(/"/g, '"') // left double quote
    .replace(/"/g, '"') // right double quote
    .replace(/…/g, '...') // ellipsis
    .replace(/％/g, '\\%') // fullwidth percent
    .replace(/𝑚/g, 'm') // mathematical italic m → regular m (from legacy)
    .replace(/&lt;/g, '<') // HTML entity less-than
    .replace(/&gt;/g, '>') // HTML entity greater-than
    .replace(/&quot;/g, '"') // HTML entity quotation mark

  // Convert ×/÷ to LaTeX commands ONLY inside $$...$$ blocks
  processed = processed.replace(/\$\$(.*?)\$\$/g, (_, content) => {
    const converted = content
      .replace(/×/g, '\\times')
      .replace(/÷/g, '\\div')
    return `$$${converted}$$`
  })

  // Convert legacy <frac> tags to CSS-based fraction marker
  // Pattern: <frac> numerator // denominator </frac>
  // Always use CSS-based fraction (problem-frac-wrap structure) for consistent rendering
  processed = processed.replace(/<frac>([\s\S]*?)<\/frac>/gi, (match, content) => {
    // Split by // but be careful about // inside {{...}}
    let separatorIndex = -1
    let depth = 0
    for (let i = 0; i < content.length - 1; i++) {
      if (content[i] === '{' && content[i + 1] === '{') depth++
      if (content[i] === '}' && content[i + 1] === '}') {
        depth--
        i++ // skip next }
        continue
      }
      if (depth === 0 && content[i] === '/' && content[i + 1] === '/') {
        separatorIndex = i
        break
      }
    }

    if (separatorIndex === -1) return match

    const num = content.substring(0, separatorIndex).trim()
    const den = content.substring(separatorIndex + 2).trim()

    // Always use CSS-based fraction marker for problem-frac-wrap rendering
    // Strip $$ from numerator and denominator if present
    const cleanNum = num.replace(/\$\$/g, '').trim()
    const cleanDen = den.replace(/\$\$/g, '').trim()
    
    // Encode {{ and }} inside the marker to prevent blank regex from splitting the marker
    // This is decoded in KatexRenderer when rendering the fraction
    // Format: [FRAC:numerator:/:denominator:FRAC] - using :/:  as separator for clarity
    const encodedNum = cleanNum.replace(/\{\{/g, '__LBLANK__').replace(/\}\}/g, '__RBLANK__')
    const encodedDen = cleanDen.replace(/\{\{/g, '__LBLANK__').replace(/\}\}/g, '__RBLANK__')
    return `[FRAC:${encodedNum}:/:${encodedDen}:FRAC]`
  })

  // Convert legacy <sqrt> tags to LaTeX \sqrt
  processed = processed.replace(/<sqrt>\s*(.*?)\s*<\/sqrt>/gi, (_, content) => {
    const cleanContent = content.replace(/\$\$/g, '').trim()
    return `$$\\sqrt{${cleanContent}}$$`
  })

  // Convert legacy <sup> tags to LaTeX superscript
  processed = processed.replace(/<sup>\s*(.*?)\s*<\/sup>/gi, (_, content) => {
    const cleanContent = content.replace(/\$\$/g, '').trim()
    return `$$^{${cleanContent}}$$`
  })

  // Convert legacy <sub> tags to LaTeX subscript
  processed = processed.replace(/<sub>\s*(.*?)\s*<\/sub>/gi, (_, content) => {
    const cleanContent = content.replace(/\$\$/g, '').trim()
    return `$$_{${cleanContent}}$$`
  })

  // Handle \hspace with missing braces: \hspace0.2ex -> \hspace{0.2ex}, \hspace10ex -> \hspace{10ex}
  // Supports both integer and decimal values
  processed = processed.replace(/\\hspace(\d+\.?\d*)([a-z]+)/g, '\\hspace{$1$2}')

  return processed
}

// Utility to substitute variables (e.g. #{a} -> 24, #{a[0]} -> first element, #{a[0]+3} -> value + 3)
export const substituteVariables = (
  text: string,
  vars: Record<string, unknown>
): string => {
  if (!vars || !text) return text || ''

  // Cast to VarRecord for internal processing
  const typedVars = vars as VarRecord

  // First, preprocess legacy markup
  let processed = preprocessLegacyMarkup(text)

  // Replace all #{...} patterns, including expressions
  processed = processed.replace(/#\{([^}]+)\}/g, (match, content) => {
    const trimmed = content.trim()

    // Simple array access: a[0]
    const simpleArrayMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\[([^\]]+)\]$/)
    if (simpleArrayMatch) {
      const [, key, indexStr] = simpleArrayMatch
      const value = getVarValue(typedVars, key, indexStr)
      if (value !== undefined) {
        return String(value)
      }
      return match
    }

    // Simple variable: a, a1
    const simpleVarMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)$/)
    if (simpleVarMatch) {
      const value = getVarValue(typedVars, trimmed)
      if (value !== undefined) {
        return String(value)
      }
      return match
    }

    // Expression (contains operators or parentheses): a[0]+3, (a[0]+1)*2
    const result = evaluateExpression(trimmed, typedVars)
    if (result !== null) {
      return result
    }

    return match
  })

  return processed
}
